import { useState, useEffect } from "react";
import { Calendar, Check, X, Clock, ChevronLeft, ChevronRight, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const statusColors = { present: "bg-green-100 text-green-700 border-green-200", half_day: "bg-yellow-100 text-yellow-700 border-yellow-200", leave: "bg-blue-100 text-blue-700 border-blue-200", absent: "bg-red-100 text-red-700 border-red-200" };
const statusIcons = { present: Check, half_day: Clock, leave: Calendar, absent: X };

export default function AttendanceTracking() {
  const { api, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [saving, setSaving] = useState(false);
  const isEmployee = user?.role === "employee";
  const canMark = ["owner", "manager", "hr_finance", "supervisor"].includes(user?.role);

  useEffect(() => {
    api().get("/employees").then(r => setEmployees(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    const params = isEmployee ? { employee_id: user?.employee_id, date: selectedDate } : { date: selectedDate };
    api().get("/attendance", { params }).then(r => setAttendance(r.data)).catch(() => {});
  }, [api, selectedDate, isEmployee, user]);

  const getStatus = (empId) => {
    const rec = attendance.find(a => a.employee_id === empId);
    return rec?.status || "absent";
  };

  const markAttendance = async (empId, status) => {
    setSaving(true);
    try {
      await api().post("/attendance/mark", { employee_id: empId, date: selectedDate, status, check_in: status === "present" ? "08:30" : status === "half_day" ? "08:30" : null, check_out: status === "present" ? "17:30" : status === "half_day" ? "12:00" : null });
      const params = { date: selectedDate };
      const res = await api().get("/attendance", { params });
      setAttendance(res.data);
      toast.success(`Attendance marked for ${employees.find(e => e.id === empId)?.name || "employee"}`);
    } catch { toast.error("Failed to mark attendance"); }
    setSaving(false);
  };

  const changeDate = (d) => {
    const dt = new Date(selectedDate);
    dt.setDate(dt.getDate() + d);
    setSelectedDate(dt.toISOString().split("T")[0]);
  };

  const present = attendance.filter(a => a.status === "present").length;
  const halfDay = attendance.filter(a => a.status === "half_day").length;
  const onLeave = attendance.filter(a => a.status === "leave").length;
  const absent = employees.length - present - halfDay - onLeave;

  return (
    <div className="space-y-6" data-testid="attendance-tracking">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif text-navy">Attendance Tracking</h2>
          <p className="text-slate-400 text-sm font-sans mt-1">{isEmployee ? "Your attendance record" : "Mark daily attendance"}</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <button onClick={() => changeDate(-1)} className="p-1 hover:bg-slate-100 rounded" data-testid="prev-date"><ChevronLeft className="w-4 h-4" /></button>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="font-mono text-sm text-navy border-none focus:outline-none bg-transparent" data-testid="date-picker" />
          <button onClick={() => changeDate(1)} className="p-1 hover:bg-slate-100 rounded" data-testid="next-date"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {!isEmployee && (
        <div className="grid grid-cols-4 gap-4">
          {[{ l: "Present", v: present, c: "bg-green-50 border-green-200 text-green-700" }, { l: "Half Day", v: halfDay, c: "bg-yellow-50 border-yellow-200 text-yellow-700" }, { l: "On Leave", v: onLeave, c: "bg-blue-50 border-blue-200 text-blue-700" }, { l: "Absent", v: absent, c: "bg-red-50 border-red-200 text-red-700" }].map((s, i) => (
            <div key={i} className={`p-4 rounded-lg border ${s.c} text-center`} data-testid={`attendance-stat-${s.l.toLowerCase().replace(" ", "-")}`}>
              <p className="text-3xl font-bold font-mono">{s.v}</p>
              <p className="text-sm font-sans">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="card-dashboard">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="attendance-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Employee</th>
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Department</th>
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Status</th>
                {canMark && <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Mark Attendance</th>}
              </tr>
            </thead>
            <tbody>
              {(isEmployee ? employees.filter(e => e.id === user?.employee_id) : employees).map(emp => {
                const status = getStatus(emp.id);
                const Icon = statusIcons[status];
                return (
                  <tr key={emp.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`attendance-row-${emp.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center"><span className="text-white text-xs font-bold">{emp.name.split(" ").map(n => n[0]).join("").slice(0, 2)}</span></div>
                        <div>
                          <p className="font-medium text-navy text-sm font-sans">{emp.name}</p>
                          <p className="text-xs text-slate-400 font-sans capitalize">{emp.salary_type === "daily_wage" ? "Daily Wage" : "Monthly"} - Rs.{emp.pay_rate}/{emp.salary_type === "daily_wage" ? "day" : "mo"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3"><span className="text-sm text-slate-600 font-sans">{emp.department}</span></td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border ${statusColors[status]}`}>
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {status === "present" ? "Present" : status === "half_day" ? "Half Day" : status === "leave" ? "Leave" : "Absent"}
                      </span>
                    </td>
                    {canMark && (
                      <td className="p-3">
                        <div className="flex gap-1.5">
                          {[{ s: "present", l: "Full", c: "bg-green-100 text-green-700 hover:bg-green-200" }, { s: "half_day", l: "Half", c: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200" }, { s: "leave", l: "Leave", c: "bg-blue-100 text-blue-700 hover:bg-blue-200" }, { s: "absent", l: "Absent", c: "bg-red-100 text-red-700 hover:bg-red-200" }].map(opt => (
                            <button key={opt.s} onClick={() => markAttendance(emp.id, opt.s)} disabled={saving}
                              className={`px-2 py-1 text-xs font-medium font-sans transition-all ${status === opt.s ? "ring-2 ring-offset-1 ring-navy/40 " : ""}${opt.c}`}
                              data-testid={`mark-${opt.s}-${emp.id}`}>{opt.l}</button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {employees.length === 0 && <p className="text-center py-8 text-slate-400 font-sans">No records found.</p>}
        </div>
      </div>
    </div>
  );
}
